import type { TDictObject } from 'src/packages/directories/types';

export type TUomSettingsField =
  | 'depthUomId'
  | 'doglegSeverityUomId'
  | 'coordinatesUomId'
  | 'toolLengthUomId'
  | 'diameterUomId'
  | 'torqueUomId'
  | 'densityUomId';

export type TUomSelectOption = {
  label: string;
  value: number;
};

export type TUomSettings = Partial<Record<TUomSettingsField, number | null>>;

export const UOM_FIELD_DEFINITIONS: ReadonlyArray<{ field: TUomSettingsField; slug: string }> = [
  { field: 'depthUomId', slug: 'well-depth' },
  { field: 'doglegSeverityUomId', slug: 'dogleg-severity' },
  { field: 'coordinatesUomId', slug: 'map-coordinates' },
  { field: 'toolLengthUomId', slug: 'tool-length' },
  { field: 'diameterUomId', slug: 'diameter' },
  { field: 'torqueUomId', slug: 'torque' },
  { field: 'densityUomId', slug: 'density' },
];

export type TUomClassOptionsResult = {
  optionsByField: Record<TUomSettingsField, TUomSelectOption[]>;
  defaultUomIdByField: Partial<Record<TUomSettingsField, number>>;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value as Record<string, unknown>;
}

function readString(record: Record<string, unknown>, key: string): string | null {
  const value = record[key];

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  return value;
}

function readNumberArray(record: Record<string, unknown>, key: string): number[] {
  const value = record[key];

  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter((item): item is number => typeof item === 'number');
}

function readNumber(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];

  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return value;
}

export function buildUomClassOptions(classOfUnits: TDictObject[], units: TDictObject[]): TUomClassOptionsResult {
  const optionsByField = Object.fromEntries(
    UOM_FIELD_DEFINITIONS.map(({ field }) => [field, [] as TUomSelectOption[]]),
  ) as Record<TUomSettingsField, TUomSelectOption[]>;

  const defaultUomIdByField: Partial<Record<TUomSettingsField, number>> = {};
  const unitIdToFields = new Map<number, TUomSettingsField[]>();

  UOM_FIELD_DEFINITIONS.forEach(({ field, slug }) => {
    const classOfUnit = classOfUnits.find((item) => readString(asRecord(item.data) ?? {}, 'slug') === slug);

    if (!classOfUnit) {
      return;
    }

    const data = asRecord(classOfUnit.data);

    if (!data) {
      return;
    }

    const defaultUomId = readNumber(data, 'defaultUomId');

    if (defaultUomId !== null) {
      defaultUomIdByField[field] = defaultUomId;
    }

    readNumberArray(data, 'unitIds').forEach((unitId) => {
      const fields = unitIdToFields.get(unitId) ?? [];
      fields.push(field);
      unitIdToFields.set(unitId, fields);
    });
  });

  units.forEach((unit) => {
    const id = Number(unit.id);
    const fields = unitIdToFields.get(id);

    if (!fields?.length) {
      return;
    }

    const data = asRecord(unit.data);

    if (!data) {
      return;
    }

    const label = readString(data, 'abbreviation') ?? readString(data, 'name');

    if (!label) {
      return;
    }

    fields.forEach((field) => {
      optionsByField[field].push({ label, value: id });
    });
  });

  return { optionsByField, defaultUomIdByField };
}

export function applyDefaultUomSettings(
  current: TUomSettings | null | undefined,
  defaultUomIdByField: Partial<Record<TUomSettingsField, number>>,
): TUomSettings | null {
  let hasChanges = false;
  const next: TUomSettings = { ...(current ?? {}) };

  UOM_FIELD_DEFINITIONS.forEach(({ field }) => {
    if (next[field] !== undefined) {
      return;
    }

    const defaultUomId = defaultUomIdByField[field];

    if (defaultUomId === undefined) {
      return;
    }

    next[field] = defaultUomId;
    hasChanges = true;
  });

  return hasChanges ? next : null;
}
